import { useEffect } from "react";
import Select from 'react-select';

import DatePicker from "react-datepicker";
import { DateInputFormat, DayMonthHeader } from "../../common/DateComponents";

import { addDays, getYear, subYears } from 'date-fns';

import Tooltip from "@/tooltip";
import { CheckCircleIcon, PlusIcon } from "@heroicons/react/20/solid";
import { connect } from 'react-redux';
import { Actions as ACTIONS } from '../../../store/actions/leaveActions';
import { validateFloat } from "../../common/validation/Validators";
import SickNotificationRow from "./SickNotificationRow";

const leaveYearOptions = [
  { label: 'Calendar Year', value: 'Calendar Year' },
  { label: 'Tax Year', value: 'Tax Year' },
  { label: 'Employee Start Date', value: 'Employee Start Date' },
  { label: 'Other', value: 'Other' }
]

const LeaveSettings = ({
  departmentOptions,
  leaveTypes,
  getLeaveTypes,
  toggleLeavePermissions,
  setLeaveLockDate,
  setLeaveFutureLockDate,
  lockDate,
  futureLockDate,
  saveLeavePermissions,
  getLeaveRules,
  saveLeaveRules,
  getLeaveSettings,
  maxDailyLeaveHours,
  payAccruedLeave,
  addPhToLeave,
  autoAddPublicHolidayLeave,
  proRataLeave,
  excludeLeaveBalance,
  updateSetting,
  disabled,
  jurisdiction,
  leaveYear,
  leaveYearDate,
  sickNotificationPeriods,
  addMultiOptionField,
  skipAutoAddPublicHolidayLeaveForWorkedDays,
  defaultPH,
}) => {
  const in45Days = addDays(new Date(), 45)

  useEffect(() => {
    getLeaveTypes()
    getLeaveSettings()
  }, []);

  return (
		<form
			className="px-8"
			onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				saveLeavePermissions();
			}}
		>
      <div className="col-xs-12 form-section">
        <div className="col-xs-12 form-container">
          <h4>Leave Settings</h4>
        </div>
        <div className="col-xs-12 form-container">
          <label className="css-input switch switch-sm switch-success" style={{ marginTop: 0 }}>
            <input disabled={disabled} checked={payAccruedLeave} id="payAccruedLeave" name="payAccruedLeave" type="checkbox" className="update-company-settings"
              onChange={(e) => {
                updateSetting(e.target.name, e.target.checked)
              }} />
            <span />Auto-Pay Accrued Leave for Casual Employees
          </label>
          <label className="css-input switch switch-sm switch-success">
            <input disabled={disabled} checked={addPhToLeave} id="addPhToLeave" name="addPhToLeave" type="checkbox" className="update-company-settings"
              onChange={(e) => {
                updateSetting(e.target.name, e.target.checked)
              }} />
            <span />Add {jurisdiction == "MT" ? 'Public' : 'Bank'} Holidays to Leave Balance
          </label>
          <label className="css-input switch switch-sm switch-success">
            <Tooltip content={`Automatically Create Leave Entries for Holidays (Disable this if your employees work on holidays)`}>
              <input disabled={disabled} checked={autoAddPublicHolidayLeave} id="autoAddPublicHolidayLeave" name="autoAddPublicHolidayLeave" type="checkbox" className="update-company-settings"
                onChange={(e) => {
                  updateSetting(e.target.name, e.target.checked)
                }} />
              <span />{`Auto-Create ${jurisdiction == "MT" ? 'Public' : 'Bank'} Holiday Leave Entries`}
            </Tooltip>
          </label>
          <label className="css-input switch switch-sm switch-success">
            <input disabled={disabled} checked={proRataLeave} id="proRataLeave" name="proRataLeave" type="checkbox" className="update-company-settings"
              onChange={(e) => {
                updateSetting(e.target.name, e.target.checked)
              }} />
            <span />Calculate leave balances on a pro-rata basis
          </label>
          <label className="css-input switch switch-sm switch-success">
            <input disabled={disabled} checked={excludeLeaveBalance} id="excludeLeaveBalance" name="excludeLeaveBalance" type="checkbox" className="update-company-settings"
              onChange={(e) => {
                updateSetting(e.target.name, e.target.checked)
              }} />
            <span />Exclude Leave Balance from Payslip
          </label>
          <label className="css-input switch switch-sm switch-success">
            <Tooltip content={`Will not add ${jurisdiction == "MT" ? 'public' : 'bank'} holiday leave if employee has timesheet or leave booked`}>
              <input disabled={disabled} checked={skipAutoAddPublicHolidayLeaveForWorkedDays} id="skipAutoAddPublicHolidayLeaveForWorkedDays" name="skipAutoAddPublicHolidayLeaveForWorkedDays" type="checkbox" className="update-company-settings"
                onChange={(e) => {
                  updateSetting(e.target.name, e.target.checked)
                }} />
              <span />Do Not Auto Add {jurisdiction == "MT" ? 'Public' : 'Bank'} Holiday Leave for Worked Days
            </Tooltip>
          </label>
        </div>
      </div>

      <div className="col-xs-12 form-section">
        <div className="col-xs-12 form-container">
          <h4>Leave Application Restrictions</h4>
        </div>
        <div className="col-xs-6">
          <div className="form-material form-container">
            <label>Maximum Daily Leave Hours</label>
            <input
              disabled={disabled}
              name="maxDailyLeaveHours"
              value={maxDailyLeaveHours}
              onChange={(e) => { updateSetting(e.target.name, validateFloat(e.target.value)) }}
              className='form-control form-input'
            />
          </div>
        </div>
        <div className="col-xs-6">
          <div className="form-material form-container">
            <label>Default {jurisdiction == "MT" ? 'Public' : 'Bank'} Holiday Hours</label>
            <input
              disabled={disabled}
              name="defaultPH"
              value={defaultPH}
              onChange={(e) => { updateSetting(e.target.name, validateFloat(e.target.value)) }}
              className='form-control form-input'
            />
          </div>
        </div>
        <div className="col-xs-6">
          <div className="form-material form-container">
            <label>Leave Lock Date</label>
            <DatePicker
              isClearable={!disabled}
              disabled={disabled}
              selected={lockDate}
              onChange={(date) => setLeaveLockDate(date)}
              minDate={subYears(new Date(), 100)}
              maxDate={in45Days}
              dateFormat="do MMM yyyy"
              customInput={<DateInputFormat />}
            />
          </div>
        </div>

        {/*Leave Lock Date */}
        <div className="col-xs-6">
          <div className="form-material form-container">
            <label>Future Leave Lock Date</label>
            <DatePicker
              isClearable={!disabled}
              disabled={disabled}
              selected={futureLockDate}
              onChange={(date) => setLeaveFutureLockDate(date)}
              minDate={new Date()}
              dateFormat="do MMM yyyy"
              customInput={<DateInputFormat />}
            />
          </div>
        </div>
      </div>

      {jurisdiction == 'UK' &&
        <>
          <div className="col-xs-12 form-section">
            <div className="col-xs-12 form-container">
              <h4>Leave Calendar Year</h4>
            </div>

            <div className="col-xs-6">
              <div className="form-material form-container">
                <span className="salary-error span-alert"></span>
                <label htmlFor="material-text">Calendar Year</label>
                <Select
                  options={leaveYearOptions}
                  name="leaveYear"
                  value={leaveYearOptions?.find(option => option.value == leaveYear) || leaveYearOptions[0]}
                  placeholder="Choose one..."
                  isDisabled={disabled}
                  onChange={(selected) => {
                    updateSetting('leaveYear', selected.value)
                  }}
                />
              </div>
            </div>
            {leaveYear == 'Other' &&
              <div className="col-xs-6">
                <div className="form-material form-container">
                  <span className="salary-error span-alert"></span>
                  <label htmlFor="material-text">Start Date</label>
                  <DatePicker
                    disabled={disabled}
                    selected={leaveYearDate && new Date(leaveYearDate)}
                    onChange={(date) => updateSetting('leaveYearDate', date)}
                    showMonthDropdown
                    minDate={new Date(getYear(new Date()), 0, 1)}
                    maxDate={new Date(getYear(new Date()), 11, 31)}
                    dateFormat="do MMMM"
                    showDisabledMonthNavigation
                    renderCustomHeader={({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => <DayMonthHeader date={date} decreaseMonth={decreaseMonth} increaseMonth={increaseMonth} prevMonthButtonDisabled={prevMonthButtonDisabled} nextMonthButtonDisabled={nextMonthButtonDisabled} />}
                    customInput={<DateInputFormat />}
                  />
                </div>
              </div>
            }
          </div>
          <div className="col-xs-12 form-section">
            <div className="col-xs-12 form-container">
              <div className="pull-left">
                <h4>Sick Notification Period</h4>
              </div>
              <div className="pull-right">
                {!disabled && sickNotificationPeriods?.length != departmentOptions?.length + 1 && departmentOptions?.length > 0 &&
                  <button
                    className="btn btn-success pull-right"
                    type="button"
                    id="add-allowance-type"
                    onClick={() => {
                      addMultiOptionField('sickNotificationPeriods')
                    }}
                  >
                    <PlusIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
                    <div style={{ float: "left" }}>Add Sick Notification Period</div>
                  </button>
                }
              </div>
            </div>
            {sickNotificationPeriods?.map((sickNotification) => <SickNotificationRow sickNotification={sickNotification} />)}
          </div>
        </>
      }

      <div className="col-xs-12 pr-0" style={{ paddingBottom: '8rem' }}>
        {!disabled &&
          <button className='btn btn-success pull-right' type="submit">
            <CheckCircleIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
            <div style={{ float: "left" }}>Save</div>
          </button>
        }
      </div>
    </form >

  );
}

const mapStateToProps = (state) => {
  const departmentOptions = state?.company?.settings?.departments?.map(dep => ({
    value: dep.id,
    label: dep.name,
  }))

  return {
    departmentOptions,
    jurisdiction: state.company?.settings?.jurisdiction,
    leaveTypes: state.leave.leaveTypes,
    lockDate: state.leave.lockDate,
    sickNotificationPeriods: state.leave?.leaveSettings?.sickNotificationPeriods,
    futureLockDate: state.leave.futureLockDate,
    maxDailyLeaveHours: state?.leave?.leaveSettings?.maxDailyLeaveHours,
    defaultPH: state?.leave?.leaveSettings?.defaultPH,
    payAccruedLeave: state?.leave?.leaveSettings?.payAccruedLeave,
    addPhToLeave: state?.leave?.leaveSettings?.addPhToLeave,
    leaveYear: state.leave?.leaveSettings?.leaveYear,
    leaveYearDate: state.leave?.leaveSettings?.leaveYearDate,
    autoAddPublicHolidayLeave: state?.leave?.leaveSettings?.autoAddPublicHolidayLeave,
    skipAutoAddPublicHolidayLeaveForWorkedDays: state?.leave?.leaveSettings?.skipAutoAddPublicHolidayLeaveForWorkedDays,
    proRataLeave: state?.leave?.leaveSettings?.proRataLeave,
    excludeLeaveBalance: state?.leave?.leaveSettings?.excludeLeaveBalance,
    disabled: (state?.permissions?.my?.permissions?.settings == 'Read') || (state?.company?.settings?.status == 'Archived')
  }
}

const mapDispatchToProps = dispatch => ({
  saveLeavePermissions: () => dispatch(ACTIONS.saveLeavePermissions()),
  getLeaveSettings: () => dispatch(ACTIONS.getLeaveSettings()),
  getLeaveTypes: () => dispatch(ACTIONS.getLeaveTypes()),
  getLeaveRules: () => dispatch(ACTIONS.getLeaveRules()),
  saveLeaveRules: () => dispatch(ACTIONS.saveLeaveRules()),
  setLeaveLockDate: (date) => dispatch(ACTIONS.setLeaveLockDate(date)),
  setLeaveFutureLockDate: (date) => dispatch(ACTIONS.setLeaveFutureLockDate(date)),
  toggleLeavePermissions: (leaveType, permission) => dispatch(ACTIONS.toggleLeavePermissions(leaveType, permission)),
  addMultiOptionField: (field) => dispatch(ACTIONS.addMultiOptionField({ parent: null, newCompany: false, field })),
  updateSetting: (field, newValue, fieldType, id) => dispatch(ACTIONS.updateSetting(field, newValue, fieldType, id))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LeaveSettings)
